import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const TEMPLATE_URL = 'https://twdhwrrzphgfksbrcsfl.supabase.co/storage/v1/object/public/docs/FORMAT%20FORMULIR%20LTB%202026.pdf';

const BerkasTambahanPage = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [existingBerkas, setExistingBerkas] = useState(null);
  const [jenis, setJenis] = useState(''); // futsal, dance, skill
  const [currentUser, setCurrentUser] = useState(null);
  
  // Search users
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    nama_tim: '',
    nama_sekolah: '',
    penanggungjawab_nama: '',
    penanggungjawab_hp: '',
    surat_rekomendasi_url: '',
    surat_pernyataan_url: '',
    ktp_official_url: '',
    kartu_pelajar_url: '',
    my_team_data: [] // [{m_personal_id, nama, email, role, foto_url, isCurrentUser}]
  });

  useEffect(() => {
    if (token && registrationId) {
      fetchAllData();
    }
  }, [token, registrationId]);

  const fetchAllData = async () => {
    try {
      // Fetch all data in parallel
      const [userRes, regRes, berkasRes] = await Promise.all([
        axios.get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/registrations/${registrationId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/berkas/registration/${registrationId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);

      // Set current user
      setCurrentUser(userRes.data);

      // Set registration and jenis
      setRegistration(regRes.data);
      const jenisKompetisi = regRes.data.m_kompetisi?.jenis?.toLowerCase() || '';
      setJenis(jenisKompetisi);

      // Get nama_sekolah from multiple sources (priority: berkas > registration > profile)
      const namaSekolah = berkasRes.data?.nama_sekolah 
        || regRes.data.data_utama?.nama_sekolah 
        || userRes.data.nama_sekolah 
        || '';

      // Set form data - prioritize existing berkas data
      if (berkasRes.data) {
        setExistingBerkas(berkasRes.data);
        setFormData({
          nama_tim: berkasRes.data.nama_tim || '',
          nama_sekolah: namaSekolah,
          penanggungjawab_nama: berkasRes.data.penanggungjawab_nama || '',
          penanggungjawab_hp: berkasRes.data.penanggungjawab_hp || '',
          surat_rekomendasi_url: berkasRes.data.surat_rekomendasi_url || '',
          surat_pernyataan_url: berkasRes.data.surat_pernyataan_url || '',
          ktp_official_url: berkasRes.data.ktp_official_url || '',
          kartu_pelajar_url: berkasRes.data.kartu_pelajar_url || '',
          my_team_data: berkasRes.data.my_team_data || []
        });
      } else {
        // No existing berkas, use nama_sekolah from available sources
        setFormData(prev => ({
          ...prev,
          nama_sekolah: namaSekolah
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data registrasi');
    } finally {
      setLoading(false);
    }
  };

  // Prepend current user to team data when registration and currentUser are loaded
  // Only runs ONCE after loading is complete to avoid overwriting form data
  useEffect(() => {
    if (registration && currentUser && !loading) {
      const currentUserData = {
        m_personal_id: currentUser.id,
        nama: currentUser.nama,
        email: currentUser.email,
        role: registration.role_kompetisi, // Use role from registration
        foto_url: currentUser.foto || '',
        isCurrentUser: true // Mark as current user so it can't be removed
      };

      setFormData(prev => {
        // Check if current user already exists in the team
        const existingIndex = prev.my_team_data.findIndex(m => m.m_personal_id === currentUser.id);
        
        if (existingIndex === -1) {
          // Current user not in team, prepend them
          return {
            ...prev,
            my_team_data: [currentUserData, ...prev.my_team_data]
          };
        } else if (existingIndex > 0) {
          // Current user exists but not at first position, move to first
          const newTeamData = [...prev.my_team_data];
          newTeamData.splice(existingIndex, 1);
          return {
            ...prev,
            my_team_data: [{ ...currentUserData, ...prev.my_team_data[existingIndex] }, ...newTeamData]
          };
        } else {
          // Current user already at first position, just update isCurrentUser flag
          const newTeamData = [...prev.my_team_data];
          newTeamData[0] = { ...newTeamData[0], isCurrentUser: true };
          return {
            ...prev,
            my_team_data: newTeamData
          };
        }
      });
    }
  }, [registration, currentUser, loading]);

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out users already in team
      const existingIds = formData.my_team_data.map(m => m.m_personal_id);
      const filtered = response.data.filter(u => !existingIds.includes(u.id));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const addTeamMember = (user, role) => {
    const newMember = {
      m_personal_id: user.id,
      nama: user.nama,
      email: user.email,
      role: role,
      foto_url: '' // Will be uploaded separately if needed
    };
    
    setFormData(prev => ({
      ...prev,
      my_team_data: [...prev.my_team_data, newMember]
    }));
    
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`${user.nama} ditambahkan ke tim`);
  };

  const removeTeamMember = (index) => {
    setFormData(prev => ({
      ...prev,
      my_team_data: prev.my_team_data.filter((_, i) => i !== index)
    }));
    toast.success('Anggota dihapus dari tim');
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      toast('Uploading...');
      const response = await axios.post(`${API}/upload/document`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: response.data.url
      }));
      
      toast.success('File berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload file');
    }
  };

  const validateForm = () => {
    if (!formData.nama_tim) {
      toast.error('Nama tim wajib diisi');
      return false;
    }
    if (!formData.nama_sekolah) {
      toast.error('Nama sekolah wajib diisi');
      return false;
    }
    if (!formData.penanggungjawab_nama) {
      toast.error('Nama penanggungjawab wajib diisi');
      return false;
    }
    if (!formData.penanggungjawab_hp) {
      toast.error('No HP penanggungjawab wajib diisi');
      return false;
    }
    if (!formData.surat_rekomendasi_url) {
      toast.error('Surat rekomendasi sekolah wajib diupload');
      return false;
    }
    if (!formData.surat_pernyataan_url) {
      toast.error('Surat pernyataan ketersediaan peserta wajib diupload');
      return false;
    }
    if (!formData.ktp_official_url) {
      toast.error('KTP Official wajib diupload');
      return false;
    }
    if (!formData.kartu_pelajar_url) {
      toast.error('Kartu Pelajar (seluruh anggota tim) wajib diupload');
      return false;
    }
    if (formData.my_team_data.length === 0) {
      toast.error('MY TEAM wajib diisi minimal 1 anggota');
      return false;
    }
    
    // Validation per jenis
    if (jenis === 'futsal') {
      // Count by role
      const players = formData.my_team_data.filter(m => 
        m.role === 'player' || m.role === 'kapten' || m.role === 'siswa'
      ).length;
      const officials = formData.my_team_data.filter(m => m.role === 'official').length;
      const koordinator = formData.my_team_data.filter(m => m.role === 'koordinator_supporter').length;
      
      if (players > 12) {
        toast.error('Maksimal 12 player (termasuk kapten) untuk futsal');
        return false;
      }
      if (officials > 2) {
        toast.error('Maksimal 2 official untuk futsal');
        return false;
      }
      if (koordinator > 1) {
        toast.error('Maksimal 1 koordinator supporter untuk futsal');
        return false;
      }
      if (players === 0) {
        toast.error('Minimal harus ada 1 player untuk futsal');
        return false;
      }
    } else if (jenis === 'dance') {
      // Count by role for dance
      const dancers = formData.my_team_data.filter(m => 
        m.role === 'dancer' || m.role === 'participant' || m.role === 'kapten' || m.role === 'siswa'
      ).length;
      const crew = formData.my_team_data.filter(m => m.role === 'crew').length;
      
      if (dancers < 6) {
        toast.error('Minimal 6 dancer untuk dance');
        return false;
      }
      if (dancers > 12) {
        toast.error('Maksimal 12 dancer untuk dance');
        return false;
      }
      if (crew > 4) {
        toast.error('Maksimal 4 supporting crew untuk dance');
        return false;
      }
    } else if (jenis === 'skill') {
      // Count by role for skill challenge
      const participants = formData.my_team_data.filter(m => 
        m.role === 'participant' || m.role === 'player' || m.role === 'kapten' || m.role === 'siswa'
      ).length;
      const officials = formData.my_team_data.filter(m => m.role === 'official').length;
      
      if (participants > 5) {
        toast.error('Maksimal 5 player untuk skill challenge');
        return false;
      }
      if (officials > 1) {
        toast.error('Maksimal 1 official untuk skill challenge');
        return false;
      }
      if (participants === 0) {
        toast.error('Minimal harus ada 1 player untuk skill challenge');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        t_registrasi_id: registrationId,
        ...formData
      };
      
      if (existingBerkas) {
        // Update
        await axios.put(`${API}/berkas/${existingBerkas.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Berkas tambahan berhasil diupdate!');
      } else {
        // Create
        await axios.post(`${API}/berkas`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Berkas tambahan berhasil disimpan!');
      }
      
      navigate('/notifications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan berkas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!registration || registration.status !== 'validated') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Akses Ditolak</h2>
          <p className="text-gray-600 mb-6">Registrasi belum divalidasi atau tidak ditemukan.</p>
          <button
            onClick={() => navigate('/home')}
            className="bg-ltb-bright text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all"
          >
            Kembali ke Home
          </button>
        </div>
      </div>
    );
  }

  // Get team limits based on jenis
  const getTeamLimits = () => {
    if (jenis === 'futsal') return { min: 1, max: 12, officials: 2, supporter: 1 };
    if (jenis === 'dance') return { min: 6, max: 12, crew: 4 };
    if (jenis === 'skill') return { min: 1, max: 5, officials: 1 };
    return { min: 1, max: 10 };
  };

  const limits = getTeamLimits();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Lengkapi Berkas Tambahan</h1>
            <p className="text-white/80 mt-1">{registration.m_event?.nama}</p>
            <p className="text-white/90 text-sm mt-1">
              {registration.m_kompetisi?.nama_kompetisi} ({registration.m_kompetisi?.jenis})
            </p>
          </div>
          <button
            onClick={() => navigate('/notifications')}
            className="bg-white/20 hover:bg-white/30 px-6 py-2 rounded-lg font-semibold transition-all"
          >
            ← Kembali
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 pb-24 mb-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Info */}
          <div className="bg-blue-50 border-l-4 border-ltb-bright p-4 mb-6">
            <p className="text-sm text-gray-700">
              <strong>Catatan:</strong> Data yang sudah disimpan masih bisa diubah hingga disetujui oleh admin.
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Tim Info */}
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Informasi Tim</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nama Tim <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nama_tim}
                    onChange={(e) => setFormData({ ...formData, nama_tim: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ltb-bright"
                    placeholder="Contoh: Tim Garuda"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nama Sekolah <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nama_sekolah}
                    onChange={(e) => setFormData({ ...formData, nama_sekolah: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ltb-bright"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nama Penanggungjawab <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.penanggungjawab_nama}
                    onChange={(e) => setFormData({ ...formData, penanggungjawab_nama: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ltb-bright"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. HP Penanggungjawab <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.penanggungjawab_hp}
                    onChange={(e) => setFormData({ ...formData, penanggungjawab_hp: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ltb-bright"
                    placeholder="08123456789"
                  />
                </div>
              </div>
            </div>

            {/* Upload Documents */}
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Upload Dokumen</h3>
              
              {/* Template Download */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Download Template Surat:</strong>
                </p>
                <a
                  href={TEMPLATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-semibold"
                >
                  📄 Download Template Formulir LTB 2026
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surat Rekomendasi Sekolah (PDF) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleFileUpload(e, 'surat_rekomendasi_url')}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {formData.surat_rekomendasi_url && (
                    <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surat Pernyataan Ketersediaan Peserta (PDF) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleFileUpload(e, 'surat_pernyataan_url')}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {formData.surat_pernyataan_url && (
                    <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    KTP Official (PDF) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleFileUpload(e, 'ktp_official_url')}
                    className="w-full px-4 py-2 border rounded-lg"
                    required={!formData.ktp_official_url}
                  />
                  {formData.ktp_official_url && (
                    <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kartu Pelajar (PDF) <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-600 mb-2 bg-blue-50 p-2 rounded">
                    ℹ️ Upload kartu pelajar <strong>seluruh anggota tim</strong> dalam satu file PDF
                  </p>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleFileUpload(e, 'kartu_pelajar_url')}
                    className="w-full px-4 py-2 border rounded-lg"
                    required={!formData.kartu_pelajar_url}
                  />
                  {formData.kartu_pelajar_url && (
                    <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
                  )}
                </div>
              </div>
            </div>

            {/* MY TEAM */}
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">MY TEAM <span className="text-red-500">*</span></h3>
              <p className="text-sm text-gray-600 mb-4">
                {jenis === 'futsal' && `Maksimal ${limits.max} pemain, ${limits.officials} official, ${limits.supporter} koordinator supporter`}
                {jenis === 'dance' && `Minimal ${limits.min} - maksimal ${limits.max} anggota, ${limits.crew} supporting crew`}
                {jenis === 'skill' && `Maksimal ${limits.max} player, ${limits.officials} official`}
              </p>

              {/* Search Users */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cari & Tambah Anggota Tim
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  placeholder="Cari berdasarkan nama, email, atau username..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ltb-bright"
                />
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div key={user.id} className="p-3 hover:bg-gray-50 border-b last:border-b-0">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-gray-800">{user.nama}</p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-gray-500">Role: {user.role}</p>
                          </div>
                          <div className="flex gap-2">
                            {jenis === 'futsal' && (
                              <>
                                <button
                                  onClick={() => addTeamMember(user, 'player')}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                >
                                  + Player
                                </button>
                                <button
                                  onClick={() => addTeamMember(user, 'official')}
                                  className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                >
                                  + Official
                                </button>
                                <button
                                  onClick={() => addTeamMember(user, 'koordinator_supporter')}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                  + Supporter
                                </button>
                              </>
                            )}
                            {jenis === 'dance' && (
                              <>
                                <button
                                  onClick={() => addTeamMember(user, 'dancer')}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                >
                                  + Dancer
                                </button>
                                <button
                                  onClick={() => addTeamMember(user, 'crew')}
                                  className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                >
                                  + Crew
                                </button>
                              </>
                            )}
                            {jenis === 'skill' && (
                              <>
                                <button
                                  onClick={() => addTeamMember(user, 'participant')}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                >
                                  + Player
                                </button>
                                <button
                                  onClick={() => addTeamMember(user, 'official')}
                                  className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                                >
                                  + Official
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searching && <p className="text-sm text-gray-500 mt-2">Mencari...</p>}
              </div>

              {/* Team Members List */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">
                  Anggota Tim ({formData.my_team_data.length})
                </p>
                {formData.my_team_data.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">Belum ada anggota tim. Cari dan tambahkan anggota di atas.</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {formData.my_team_data.map((member, index) => (
                      <div 
                        key={index} 
                        className={`p-4 rounded-lg flex justify-between items-center ${
                          member.isCurrentUser 
                            ? 'bg-purple-50 border-2 border-purple-200' 
                            : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">{member.nama}</p>
                            {member.isCurrentUser && (
                              <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                                Anda
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{member.email}</p>
                          <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {member.role}
                          </span>
                        </div>
                        {!member.isCurrentUser && (
                          <button
                            onClick={() => removeTeamMember(index)}
                            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                          >
                            Hapus
                          </button>
                        )}
                        {member.isCurrentUser && (
                          <span className="text-xs text-gray-500 italic">
                            (Tidak dapat dihapus)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                onClick={() => navigate('/notifications')}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
              >
                Batal
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-3 bg-gradient-ltb-r text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {submitting ? 'Menyimpan...' : existingBerkas ? 'Update Berkas' : 'Simpan Berkas'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BerkasTambahanPage;
